export const ACCESS_TOKEN_QUERY_PARAM = 'accessToken'
export const STORED_ACCESS_TOKEN_KEY = 'okclaw:webui:access-token'

export type AccessTokenSource = 'url' | 'storage' | 'none'

export type AccessTokenState = {
  token: string
  source: AccessTokenSource
}

type AccessTokenStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function getDefaultStorage(): AccessTokenStorage | undefined {
  if (typeof window === 'undefined') return undefined
  return window.localStorage
}

function readUrlAccessToken(href: string): string {
  try {
    const url = new URL(href)
    return url.searchParams.get(ACCESS_TOKEN_QUERY_PARAM)?.trim() ?? ''
  } catch {
    return ''
  }
}

export function readStoredAccessToken(storage = getDefaultStorage()): string {
  if (!storage) return ''
  try {
    return storage.getItem(STORED_ACCESS_TOKEN_KEY)?.trim() ?? ''
  } catch {
    return ''
  }
}

export function storeAccessToken(accessToken: string, storage = getDefaultStorage()): void {
  const token = accessToken.trim()
  if (!storage || !token) return
  try {
    storage.setItem(STORED_ACCESS_TOKEN_KEY, token)
  } catch {
    // Best effort: private browsing or locked-down storage should not block login.
  }
}

export function clearStoredAccessToken(storage = getDefaultStorage()): void {
  if (!storage) return
  try {
    storage.removeItem(STORED_ACCESS_TOKEN_KEY)
  } catch {
    // Best effort.
  }
}

export function shouldClearStoredAccessToken(error: unknown, invalidTokenMessage: string): boolean {
  if (error instanceof Error) {
    const code = (error as Error & { code?: unknown }).code
    const kind = (error as Error & { kind?: unknown }).kind
    if (typeof kind === 'string' && kind.toLowerCase() === 'auth') return true
    if (typeof code === 'string' && code.toUpperCase() === 'AUTH_FAILED') return true
    if (typeof code === 'string' && code.toUpperCase() === 'WS_CLOSE_4005') return true
    return error.message === invalidTokenMessage
  }

  return String(error) === invalidTokenMessage
}

export function resolveAccessTokenState(href: string, storage = getDefaultStorage()): AccessTokenState {
  const urlToken = readUrlAccessToken(href)
  const storedToken = readStoredAccessToken(storage)

  if (urlToken) {
    return {
      token: urlToken,
      source: storedToken && storedToken === urlToken ? 'storage' : 'url',
    }
  }

  if (storedToken) {
    return {
      token: storedToken,
      source: 'storage',
    }
  }

  return {
    token: '',
    source: 'none',
  }
}

export function resolveInitialAccessToken(href: string): string {
  return resolveAccessTokenState(href).token
}

export function shouldAutoInitializeAccessToken(href: string, state: AccessTokenState): boolean {
  if (!state.token) return false
  if (state.source === 'storage') return true

  try {
    const url = new URL(href)
    const segments = url.pathname.split('/').filter(Boolean)
    return state.source === 'url' && segments[0] === 'd' && Boolean(segments[1])
  } catch {
    return false
  }
}

export function buildAccessTokenHeaders(accessToken: string): HeadersInit {
  const token = accessToken.trim()
  return token
    ? { Authorization: `Bearer ${token}` }
    : {}
}
